import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import ModalHeader from 'Modals/components/ModalHeader';
import ModalBody from 'Modals/components/ModalBody';
import ModalFooter from 'Modals/components/ModalFooter';
import LoadingBarModalBody from 'Modals/components/LoadingBarModalBody';

// Setting width and height to 0 prevents tabbing through fields: https://github.com/reactjs/react-modal/blob/v3.3.2/src/helpers/tabbable.js#L16
// Let's just make the background content that is #fff by default transparent
export const defaultModalStyle = {
  overlay: {
    background: 'rgba(0, 0, 0, 0.8)',
    zIndex: 99998,
  },
  content: {
    border: 'none',
    background: 'transparent',
    margin: 0,
    overflow: 'visible',
  },
};

/**
 * @deprecated since `creating_new_modals` ADR. `Use DesignSystem/FormSwiftModal`
 */
export default class BaseModal extends React.Component {
  static validElements(elmArray) {
    const invalid = elmArray.filter(elm => !React.isValidElement(elm));
    if (invalid.length > 0) {
      return false;
    }

    return true;
  }

  constructor(props) {
    super(props);
    this.closeModal = this.closeModal.bind(this);
    this.onHeaderCloseModal = this.closeModal.bind(this, null);
    this.renderFooterButtons = this.renderFooterButtons.bind(this);
    this.state = {
      isModalOpen: this.props.activeModal === this.props.modalType,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.activeModal !== nextProps.activeModal &&
      (this.props.activeModal === this.props.modalType ||
        nextProps.activeModal === this.props.modalType
      )
    ) {
      this.setState({ isModalOpen: this.props.modalType === nextProps.activeModal });
    }
  }

  closeModal(callback) {
    this.props.closeModal(this.props.modalType);

    if (callback) {
      callback();
    }
  }

  renderFooterButtons(content) {
    // This recursively maps through the footer content to ensure button elements
    // are cloned as well as fragments, which is not available on React 16.0
    // https://reactjs.org/blog/2017/11/28/react-v16.2.0-fragment-support.html
    return React.Children.map(content, (child) => {
      if (child.props.children && BaseModal.validElements(child.props.children)) {
        return React.cloneElement(child, {
          children: this.renderFooterButtons(child.props.children),
        });
      }

      let trackAction = child.props.trackAction;
      if (this.props.trackFreeWrite) {
        trackAction = `${window.DocumentSettings.type} | FreeWrite-Builder${this.props.additionalData.memberTracking}`;
      }

      return React.cloneElement(child, {
        buttonOnClick: this.closeModal.bind(this, child.props.buttonClick),
        trackAction,
      });
    });
  }

  render() {
    return (
      <Modal
        isOpen={this.state.isModalOpen}
        style={this.props.modalStyle || defaultModalStyle}
        contentLabel={this.props.ariaLabel}
        onAfterOpen={this.props.afterOpen}
        onRequestClose={this.props.preventClickingOutside ? () => {} : this.onHeaderCloseModal}
        ariaHideApp={false}
        className={this.props.modalClass}
        shouldCloseOnOverlayClick={this.props.shouldCloseOnOverlayClick}
        shouldCloseOnEsc={this.props.shouldCloseOnEsc}
      >
        <div className={this.props.modalClass}>
          {(this.props.headerText || this.props.includeHeaderCloseButton) &&
            <ModalHeader
              headingText={this.props.headerText}
              headingClass={this.props.headerClass}
              subHeadingText={this.props.subHeaderText}
              onClose={this.onHeaderCloseModal}
              includeHeaderCloseButton={this.props.includeHeaderCloseButton}
            />
          }
          {this.props.bodyContent &&
            <ModalBody modalBodyClass={this.props.modalBodyClass}>
              {React.Children.map(this.props.bodyContent, child => (
                React.cloneElement(child, {
                  closeModal: this.closeModal,
                })
              ))}
            </ModalBody>
          }
          {this.props.isAnimated &&
            this.props.loadOptions &&
            this.props.loadOptions.loadingBarContent &&
            <LoadingBarModalBody
              isDynamicContent={this.props.loadOptions.isDynamicContent}
              loadSteps={this.props.loadOptions.loadSteps}
              finalStep={this.props.finalStep}
              isAnimating={this.props.isAnimating}
              additionalData={this.props.additionalData}
              loadingBarClasses={this.props.loadOptions.loadingBarClasses}
              loadingModalBodyClasses={this.props.loadOptions.loadingModalBodyClasses}
            >
              {this.props.loadOptions.loadingBarContent}
            </LoadingBarModalBody>
          }
          {this.props.children}
          {this.props.footerContent &&
            <ModalFooter modalFooterClass={this.props.footerClass}>
              {
                this.props.footerButtons ?
                this.renderFooterButtons(this.props.footerContent) :
                this.props.footerContent
              }
            </ModalFooter>
          }
        </div>
      </Modal>
    );
  }
}

BaseModal.propTypes = {
  modalType: PropTypes.string.isRequired,
  modalStyle: PropTypes.shape(),
  closeModal: PropTypes.func.isRequired,
  children: PropTypes.node,
  additionalData: PropTypes.shape({
    memberTracking: PropTypes.string,
  }),
  modalClass: PropTypes.string,
  isAnimated: PropTypes.bool,
  isAnimating: PropTypes.bool,
  isDynamicContent: PropTypes.bool,
  activeModal: PropTypes.string,
  headerText: PropTypes.string,
  headerClass: PropTypes.string,
  subHeaderText: PropTypes.string,
  bodyContent: PropTypes.node,
  modalBodyClass: PropTypes.string,
  footerContent: PropTypes.node,
  footerClass: PropTypes.string,
  footerButtons: PropTypes.bool,
  afterOpen: PropTypes.func,
  loadOptions: PropTypes.shape({
    loadingBarContent: PropTypes.node,
    isDynamicContent: PropTypes.bool,
    loadSteps: PropTypes.arrayOf(PropTypes.shape()),
    loadingBarClasses: PropTypes.string,
    loadingModalBodyClasses: PropTypes.string,
  }),
  finalStep: PropTypes.shape({
    percent: PropTypes.number,
    interval: PropTypes.number,
    content: PropTypes.shape({
      renderingFontsPercent: PropTypes.number,
      loadingPagesPercent: PropTypes.number,
      initializingEditorPercent: PropTypes.number,
      finalizingPercent: PropTypes.number,
    }),
  }),
  trackFreeWrite: PropTypes.bool,
  ariaLabel: PropTypes.string,
  includeHeaderCloseButton: PropTypes.bool,
  shouldCloseOnOverlayClick: PropTypes.bool,
  shouldCloseOnEsc: PropTypes.bool,
  preventClickingOutside: PropTypes.bool,
};

BaseModal.defaultProps = {
  children: null,
  additionalData: {
    memberTracking: null,
  },
  modalClass: 'modal',
  modalStyle: null,
  isAnimated: false,
  isAnimating: false,
  isDynamicContent: false,
  activeModal: null,
  headerText: null,
  headerClass: 'centered',
  subHeaderText: null,
  bodyContent: null,
  modalBodyClass: 'modal-body',
  footerContent: null,
  footerClass: 'modal-footer',
  footerButtons: false,
  afterOpen: () => (true),
  loadingBarContent: null,
  autoStartAnimation: null,
  loadOptions: null,
  finalStep: null,
  trackFreeWrite: false,
  ariaLabel: null,
  includeHeaderCloseButton: false,
  shouldCloseOnOverlayClick: true,
  shouldCloseOnEsc: true,
  preventClickingOutside: false,
};



// WEBPACK FOOTER //
// ./src-react/Modals/BaseModal/index.jsx